import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { BlogPostBody } from "@/components/blog/BlogPostBody";
import { fmtDate } from "@/lib/blog-parser";

export const Route = createFileRoute("/blog/$slug")({
  // Imported dynamically because this loader lives in the main route tree —
  // a static import would put every compiled post in the chunk that *every*
  // page downloads. Readers already get the prerendered HTML, so the extra
  // round trip here is invisible.
  loader: async ({ params }) => {
    const { findPost } = await import("@/lib/blog");
    const post = findPost(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { post } = loaderData;
    return {
      meta: [
        { title: `${post.meta.title} — Busca Blindados` },
        { name: "description", content: post.meta.description },
        { property: "og:title", content: post.meta.title },
        { property: "og:description", content: post.meta.description },
        { property: "og:type", content: "article" },
        ...(post.meta.ogImage ? [{ property: "og:image", content: post.meta.ogImage }] : []),
      ],
    };
  },
  component: BlogPostPage,
});

function BlogPostPage() {
  const { post } = Route.useLoaderData();
  return (
    <div className="min-h-screen text-foreground">
      <AppNav maxWidth="max-w-6xl" />
      <main className="mx-auto max-w-3xl px-6 pb-32 pt-6">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Voltar pro blog
        </Link>

        <article className="mt-6">
          <header className="mb-8">
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {fmtDate(post.meta.date)}
              {post.meta.lastUpdated && post.meta.lastUpdated > post.meta.date && (
                <span className="ml-2 text-muted-foreground/70 normal-case tracking-normal">
                  · Atualizado em {fmtDate(post.meta.lastUpdated)}
                </span>
              )}
            </p>
            <h1 className="mt-2 font-display text-3xl text-foreground sm:text-4xl">
              {post.meta.title}
            </h1>
            {/* Lede — same string as SERP meta description, styled as a dek
                so the reader doesn't take it for the article opening. */}
            <p className="mt-4 border-l-2 border-primary/40 pl-4 text-base italic leading-relaxed text-foreground/75 sm:text-lg">
              {post.meta.description}
            </p>
          </header>

          <BlogPostBody blocks={post.blocks} />
        </article>

        {/* CTA — every post nudges back into the product. Primary action goes
            to the alert-creation flow (matches the copy "te avisa por email").
            Secondary link covers the "I just want to browse" reader. */}
        <aside className="mt-16 rounded-2xl border border-primary/20 bg-primary/5 p-8">
          <h3 className="font-display text-xl text-foreground sm:text-2xl">
            Quer monitorar carros blindados?
          </h3>
          <p className="mt-2 text-base text-muted-foreground">
            A gente acompanha 60+ sites e te avisa por email quando aparece anúncio novo que bate com o teu filtro.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            {/* Plain <a> so the browser handles the #criar-alerta scroll on
                the home — TanStack <Link> + hash needs extra props and adds
                nothing over native anchor behaviour for a single CTA. */}
            <a
              href="/#criar-alerta"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
            >
              Criar alerta grátis
              <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to="/buscar"
              className="text-sm text-muted-foreground transition hover:text-foreground"
            >
              ou explorar o catálogo →
            </Link>
          </div>
        </aside>
      </main>
    </div>
  );
}
